/* eslint-disable */
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ErrorHandler,
  InjectionToken,
  NgModule,
  Optional,
  provideAppInitializer,
  SkipSelf,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';

import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ICON_SETTINGS } from '@progress/kendo-angular-icons';

// libs
import {
  provideTranslateService,
  TranslateLoader,
  TranslateModule,
} from '@ngx-translate/core';
import {
  provideTranslateHttpLoader,
  TranslateHttpLoader,
} from '@ngx-translate/http-loader';
// import { PerDiemService } from '@ups/xplat/api/services';

import {
  applyApiUrlOverrides,
  CacheCollectionService,
  CoreModule,
  environment,
  ExportService,
  LiveUpdateService,
  LocalStorageService,
  OfflineStorageService,
  PlatformDeviceInfoToken,
  PlatformLanguageToken,
  PlatformWindowToken,
  ROUTE_TRACKING_CONFIG,
  RouterToken,
  RouteTrackingConfig,
  RouteTrackingService,
  SessionStorageService,
  SignalRPlatformService,
} from '@ups/xplat/core';
import {
  FeatureSwitch,
  throwIfAlreadyLoaded,
} from '@ups/xplat/utils';

import {
  GlobalErrorHandler,
  MessageHelper,
  NotificationsViewService,
  PROVIDERS,
  WebLiveUpdateService,
} from './services';
import { WebCacheCollectionService } from './services/web-cache-collection.service';
import { WebExportService } from './services/web-export.service';
import { WebNetworkService } from './services/web-network.service';
import { WebOfflineStorageService } from './services/web-offline-storage.service';
import { WebSignalRPlatformService } from './services/web-signal-r-platform.service';
// import { WebMicrosoftClarityService } from './services/web-microsoft-clarity.service';
import { RynReplayRecorderService } from './services/ryn-replay-recorder.service';
import { getAppVersionInfo } from '@ups/xplat/web/utils';

// bring in custom web services here...

// factories
export function winFactory() {
  return window;
}

export function platformLangFactory() {
  const browserLang = window.navigator.language || 'en'; // fallback English
  // browser language has 2 codes, ex: 'en-US'
  return browserLang.split('-')[0];
}

export function platformDeviceInfoFactory() {
  /**
   * Done here to ensure version info is available to the earliest api calls made on app launch
   */
  // TODO: web team - you could fill this in with various web info
  const versionName = '1.0.0';
  const buildNumber = '1.0.0';
  const deviceDetails = 'browser'; // this can be a collection of any client details
  // console.log('version:', versionName, ' versionDisplay:', `v${versionName} (${buildNumber})`);
  return {
    version: versionName,
    versionDisplay: `v${versionName} (${buildNumber})`,
    os: 'web', // could report client platform (or browser vendor)
    deviceDetails,
  };
}

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-center',
      timeOut: 10000,
      preventDuplicates: true,
    }),
    CoreModule.forRoot([
      FeatureSwitch.globalErrorHandlerEnabled
        ? {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler,
          }
        : {
            provide: new InjectionToken('empty'),
            useValue: null,
          },
      {
        provide: PlatformLanguageToken,
        useFactory: platformLangFactory,
      },
      {
        provide: PlatformWindowToken,
        useFactory: winFactory,
      },
      {
        provide: PlatformDeviceInfoToken,
        useFactory: platformDeviceInfoFactory,
      },
      {
        provide: RouterToken,
        // just nullify: we rely on Router injection in ui.effect
        // however the token at least needs a provider
        useValue: null,
      },
      {
        provide: OfflineStorageService,
        useClass: WebOfflineStorageService,
      },
      LocalStorageService,
      SessionStorageService,
      {
        provide: CacheCollectionService,
        useClass: WebCacheCollectionService,
      },
      {
        provide: SignalRPlatformService,
        useClass: WebSignalRPlatformService,
      },
      {
        provide: LiveUpdateService,
        useClass: WebLiveUpdateService,
      },
      {
        provide: ExportService,
        useClass: WebExportService,
      },
    ]),
    // Instrumentation must be imported after importing StoreModule (config is optional)
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: false, // Restrict extension to log-only mode
    }),
  ],
  providers: [
    { provide: ICON_SETTINGS, useValue: { type: 'font' } },
    ...PROVIDERS,
    // Dev/PR-only: repoint environment.urls.* per any active override BEFORE any
    // component reads them, so direct reads (Syncfusion host, hand-built URIs)
    // are covered too — not just MyHttpClientFactory traffic. No-op on stg/prod.
    provideAppInitializer(() => applyApiUrlOverrides()),
    // No change-detection provider here on purpose (WI 32149). UpsCoreModule is
    // imported into the root injector of every web app, both zone-based and
    // zoneless (web-ryns-roost, web-ryn-control-center, web-ryn-replay). An
    // unconditional provideZoneChangeDetection() here collided with those apps'
    // provideZonelessChangeDetection() and logged NG0408 at bootstrap. Each app
    // owns its CD strategy in its own bootstrap; the shared module stays
    // CD-agnostic. Angular 21 defaults to ZONELESS, so a zone-based app must
    // call provideZoneChangeDetection() in its own app.module.ts (they all do) —
    // removing it here does NOT leave those apps on a zone default, there is
    // none. Re-adding any CD provider here re-breaks the zoneless apps.
    provideHttpClient(withInterceptorsFromDi()),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json',
      }),
    }),
    {
      provide: ROUTE_TRACKING_CONFIG,
      useValue: {
        applicationId: environment.security.appId,
        applicationEnvironment:
          environment.environment_name,
        domain: location.hostname,
        applicationVersion:
          getAppVersionInfo().versionName ?? '',
      } as RouteTrackingConfig,
    },
  ],
})
export class UpsCoreModule {
  constructor(
    @Optional()
    @SkipSelf()
    parentModule: UpsCoreModule,
    // global web behavior
    // perDiemService: PerDiemService,
    webNetworkService: WebNetworkService,
    // init singleton services
    private messageHelper: MessageHelper,
    private notifications: NotificationsViewService, // private clarity: WebMicrosoftClarityService
    // Ryn Replay (#30070). Injected so the recorder starts at app boot, before
    // any feature module loads, or the first page of every session is missing.
    // Constructed in EVERY app and safe by design: with no
    // `environment.rynReplay.enabled` its constructor returns immediately, and
    // the backend token defaults to the no-op implementation, so a
    // non-participating app loads no rrweb chunk and attaches no listeners.
    // WebSmartlookService is the legacy inject seam; it forwards to Ryn Replay.
    private rynReplay: RynReplayRecorderService,
    private routeTrackingService: RouteTrackingService
  ) {
    throwIfAlreadyLoaded(parentModule, 'UpsCoreModule');

    // perDiemService.showMessage$.subscribe((res) => {
    //   switch (res.type) {
    //     case 'error':
    //       this.messageHelper.error(res.message);
    //       break;
    //     case 'information':
    //       this.messageHelper.information(res.message);
    //       break;
    //     case 'success':
    //       this.messageHelper.success(res.message);
    //       break;
    //   }
    // });

    // web offline behavior handling
    webNetworkService.initOfflineHandling();

    routeTrackingService.enable();
  }
}
